import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ } from "@/constants/testIds/landing";
import Reveal from "@/components/site/Reveal";

/**
 * Editorial FAQ — Skandi magazine "Q&A column".
 * Asymmetric two-column layout with a small kicker index on the left
 * and elegant Nº-numbered accordion items on the right.
 */

const items = [
  {
    q: 'Wie funktioniert die Förderung über den Bildungsgutschein?',
    a: 'Als AZAV-zertifizierter Bildungsträger können ausgewählte Kurse zu 100 % über den Bildungsgutschein der Agentur für Arbeit oder das Jobcenter gefördert werden – aktuell die Pflegeassistenz LG1 & LG2, die Ambulante Pflegeassistenz und die Pflegehilfskraft (429 UE). Wir unterstützen dich beim gesamten Antragsprozess – kostenlos und unverbindlich.',
    theme: 'Förderung',
  },
  {
    q: 'Welche Voraussetzungen brauche ich für die Weiterbildung?',
    a: 'Die Voraussetzungen variieren je nach Kurs. Einstiegskurse (z. B. Pflegeassistenz QN2) sind ohne Vorerfahrung möglich. Für weiterführende Qualifikationen wie LG1/LG2 oder PDL ist eine Pflege-Grundausbildung oder Berufserfahrung Voraussetzung. In der kostenlosen Beratung klären wir das gemeinsam.',
    theme: 'Zulassung',
  },
  {
    q: 'Wie läuft der Online-Unterricht ab?',
    a: 'Live via Zoom – mit echten Dozent:innen, interaktiv und mit Praxisbezug. Du lernst von überall in Deutschland, behältst Job/Familie im Alltag und sparst Anfahrtswege. Bei vielen Kursen lernst du flexibel über unsere moderne E-Learning-Plattform Modul für Modul, dazu kommen Live-Termine nach Absprache mit der Dozentin.',
    theme: 'Unterricht',
  },
  {
    q: 'Sind die Abschlüsse anerkannt?',
    a: 'Ja. Dickies-Campus ist staatlich anerkannte Bildungseinrichtung und AZAV-zertifiziert. Unsere Abschlüsse erfüllen die jeweiligen gesetzlichen Vorgaben (z. B. §43b, §53c SGB XI, PflAPrV §4, LG1/LG2 nach Landesrahmenvertrag NRW).',
    theme: 'Anerkennung',
  },
  {
    q: 'Wann kann ich starten?',
    a: 'Bei den Lehrgängen LG1/LG2, Ambulante Pflegeassistenz, Pflegehilfskraft und Pflegeassistenz QN2 jeden Montag. Bei allen anderen Weiterbildungen kannst du sofort starten und die Zeiten individuell mit der Dozentin abstimmen.',
    theme: 'Kursstart',
  },
  {
    q: 'Welche Zahlarten kann ich nutzen?',
    a: 'Du kannst online direkt mit Kreditkarte, Klarna, SEPA-Lastschrift oder über den Bildungsgutschein zahlen — oder in 3, 6 oder 12 Monatsraten ohne Aufschlag. Alle Buchungen laufen sicher und verschlüsselt über Stripe.',
    theme: 'Zahlung',
  },
  {
    q: 'Bietet ihr Träger-Programme für Pflegedienste an?',
    a: 'Ja. Für Pflegedienste und Einrichtungen bieten wir individuelle Online-Pakete und Kooperationen an – deutschlandweit, inklusive Pflichtfortbildungen (auf MD-Prüfungen vorbereitet) und maßgeschneiderter Qualifizierungswege für ganze Teams. Als Premium Partner gibt es zusätzlich 20 % Dauerrabatt und kostenlose Pflichtfortbildungen.',
    theme: 'Für Träger',
  },
];

export { items as faqItems };


export default function Faq() {
  return (
    <section id="faq" className="bg-cream-100 py-28 md:py-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[4fr_8fr] gap-12 lg:gap-20 items-start">
          {/* LEFT · sticky editorial column */}
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Häufige Fragen
                </span>
              </div>
              <h2 className="font-magazine font-medium text-sand-800 leading-[1.08] text-[2.6rem] sm:text-[3.4rem] lg:text-[3.6rem]">
                Alles, was du <br />
                <span className="text-terra italic underline decoration-saffron-dark decoration-[3px] underline-offset-[10px]">wissen musst.</span>
              </h2>
              <p className="mt-6 text-sand-700 text-[15px] leading-[1.7] max-w-sm">
                Sieben klare Antworten. Wenn deine Frage nicht dabei ist —
                greif zum Hörer.
              </p>
              <a
                href="tel:+4921129273330"
                data-testid="faq-phone-cta"
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold text-sand-800 border-b border-terra pb-1 hover:text-[#96482A] transition-colors"
              >
                0211 29273330
              </a>
            </div>
          </Reveal>

          {/* RIGHT · editorial Q&A list — no rounded card boxes */}
          <Reveal delay={120}>
            <Accordion
              type="single"
              collapsible
              defaultValue="item-0"
              data-testid={FAQ.accordion}
              className="divide-y divide-sand-300/60 border-t border-sand-300/60"
            >
              {items.map((it, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  data-testid={`${FAQ.item}-${idx}`}
                  className="border-none"
                >
                  <AccordionTrigger
                    className="group py-6 md:py-7 hover:no-underline items-start text-left"
                  >
                    <div className="flex items-start gap-6 flex-1 min-w-0 pr-4">
                      {/* Editorial Nº index */}
                      <div className="shrink-0 w-[6rem] md:w-[6rem] pt-1">
                        <div className="font-magazine text-[13px] italic text-terra tabular-nums">
                          Nº {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div className="mt-1 text-[9.5px] uppercase tracking-[0.24em] text-sand-500 font-semibold">
                          {it.theme}
                        </div>
                      </div>
                      {/* Question */}
                      <div className="min-w-0 flex-1">
                        <div className="font-magazine text-[1.05rem] md:text-[1.2rem] text-sand-800 leading-tight group-data-[state=open]:text-[#96482A] transition-colors">
                          {it.q}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-4">
                    <div className="ml-0 md:ml-[7.5rem] max-w-2xl">
                      <p className="text-sand-700 text-[14.5px] leading-[1.8]">
                        {it.a}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
